import { useId, useState, type ReactNode } from 'react'
import { AlertTriangle, Check, ShieldAlert } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ActionCardPreview, ActionCardState, ActionCardTier } from '@/lib/chatTypes'

export type ActionCardProps = {
  tier: ActionCardTier
  title: string
  description?: string
  state: ActionCardState
  primaryLabel?: string
  secondaryLabel?: string
  onPrimary?: () => void
  onSecondary?: () => void
  // CTAs visibles mais désactivés (ex. réponse de l'agent en cours).
  actionsDisabled?: boolean
  resultText?: string
  // Story 7.1 — message d'erreur (échec, budget emails, expiration).
  errorText?: string
  // Story 7.1 (PO Q3) — lien proposé sur une carte exécutée (ex. « Ajouter des
  // fournisseurs » après createCampaign → vue Campagnes).
  resultLink?: { label: string; onClick: () => void }
  // Story 7.2 (PO Q2) — détail dépliable « Voir avant d'envoyer » (destinataires,
  // objet + message), visible uniquement en état `proposed`.
  preview?: ActionCardPreview
  icon?: ReactNode
  className?: string
}

const STATE_LABELS: Record<ActionCardState, string> = {
  proposed: 'À confirmer',
  confirmed: 'Confirmée',
  executed: 'Exécutée',
  cancelled: 'Annulée',
  failed: 'Échec',
  expired: 'Non confirmée',
}

const FOCUS_RING = 'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring'

// Carte de confirmation Tier 2/3, purement présentationnelle : l'état et les
// callbacks viennent des props. Story 7.1 : en état `confirmed` (fenêtre
// d'annulation de 5 s), seul le bouton secondaire « Annuler (n s) » est rendu.
export function ActionCard({
  tier,
  title,
  description,
  state,
  primaryLabel = 'Confirmer',
  secondaryLabel = 'Annuler',
  onPrimary,
  onSecondary,
  actionsDisabled = false,
  resultText,
  errorText,
  resultLink,
  preview,
  icon,
  className,
}: ActionCardProps) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const previewId = useId()
  const isDone = state === 'executed' || state === 'cancelled' || state === 'failed' || state === 'expired'
  const isUndoWindow = state === 'confirmed' && Boolean(onSecondary)
  const showActions = (state === 'proposed' && (onPrimary || onSecondary)) || isUndoWindow
  const DefaultIcon = tier === 3 ? ShieldAlert : AlertTriangle

  return (
    <Card
      role="alert"
      data-state={state}
      className={cn(
        'gap-2 border-amber-200 bg-[var(--chat-confirmation-bg)] p-4',
        isDone && 'opacity-60',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="mt-0.5 text-amber-600">
          {state === 'executed' ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            icon ?? <DefaultIcon className="h-4 w-4" />
          )}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium text-slate-900">{title || 'Action à confirmer'}</p>
            <Badge variant="outline" className="text-[10px]">
              {STATE_LABELS[state]}
            </Badge>
          </div>
          {description ? <p className="text-sm text-slate-600">{description}</p> : null}
          {preview && state === 'proposed' ? (
            <div>
              <button
                type="button"
                aria-expanded={previewOpen}
                aria-controls={previewId}
                onClick={() => setPreviewOpen((open) => !open)}
                className={cn('rounded text-xs font-medium text-slate-700 underline underline-offset-2', FOCUS_RING)}
              >
                {previewOpen ? 'Masquer' : "Voir avant d'envoyer"}
              </button>
              <div
                id={previewId}
                hidden={!previewOpen}
                className="mt-2 space-y-2 rounded-md bg-white/70 p-2 text-xs text-slate-700"
              >
                <p className="font-medium">{preview.label}</p>
                <ul className="list-disc space-y-0.5 pl-4">
                  {preview.items.map((item, i) => (
                    <li key={`${i}-${item}`}>{item}</li>
                  ))}
                </ul>
                {preview.text ? <p className="whitespace-pre-line">{preview.text}</p> : null}
              </div>
            </div>
          ) : null}
          {state === 'executed' && resultText ? (
            <p className="text-xs text-slate-600">✓ {resultText}</p>
          ) : null}
          {errorText ? (
            <p role="status" className="text-xs text-red-700">
              {errorText}
            </p>
          ) : null}
        </div>
      </div>
      {showActions ? (
        <div className="flex gap-2 pl-7">
          {onPrimary && !isUndoWindow ? (
            <Button size="sm" onClick={onPrimary} disabled={actionsDisabled} className={FOCUS_RING}>
              {primaryLabel}
            </Button>
          ) : null}
          {onSecondary ? (
            <Button
              size="sm"
              variant="outline"
              onClick={onSecondary}
              disabled={actionsDisabled && !isUndoWindow}
              className={FOCUS_RING}
            >
              {secondaryLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
      {state === 'executed' && resultLink ? (
        <div className="pl-7">
          <Button size="sm" variant="outline" onClick={resultLink.onClick} className={FOCUS_RING}>
            {resultLink.label}
          </Button>
        </div>
      ) : null}
    </Card>
  )
}
